import React from "react";
import { GlobalApplicationState } from "globalApplicationState";
import { ConnectedProps, connect, useDispatch } from "react-redux";
import TabContentToolbar from "pages/common/tabContentToolbar";

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded';
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button } from "@mui/material";
import { TopicDetails, TopicGroup } from "../models";
import { tagsApi } from "api/instances";
import TopicMove from "./dialogs/topicMove";
import TopicEdit from "./dialogs/topicEdit";
import { TopicDeletionHandler } from "../utils/topicDeletionHandler";
import { TopicReportHandler } from "../utils/topicReportHandler";
import { ReportTypes } from "modules/reports/models";
import SuccessSnackbar from "modules/common/components/snackbars/successSnackbar";
import TopicViewDetails from "./dialogs/details/topicViewDetails";
import { editTopicState } from "../reducer";
import { SNACKBAR_TIME_MS } from "../styles/constants";

const TopicToolbar: React.FunctionComponent<PropsWithRedux> = (props) => {
    const [topicDetails, setTopicDetails] = React.useState<TopicDetails | undefined>(undefined);

    const [successMessage, setSuccessMessage] = React.useState("");

    const [topicDetailsOn, setTopicDetailsOn] = React.useState(false);
    const [editDialogOn, setEditDialogOn] = React.useState(false);
    const [moveDialogOn, setMoveDialogOn] = React.useState(false);

    const { selected } = props;

    const dispatch = useDispatch();

    const openEditDialog = () => setEditDialogOn(true);

    const closeEditDialog = () => setEditDialogOn(false);

    const openMoveDialog = () => setMoveDialogOn(true);

    const closeMoveDialog = () => setMoveDialogOn(false);

    const openDetailsDialog = () => setTopicDetailsOn(true);

    const closeDetailsDialog = () => setTopicDetailsOn(false);

    const handleStateUpdate = (disable: boolean) =>
        dispatch(editTopicState({ topics: selected, disable }));

    const handleDownload = async () => {
        if (!topicDetails || selected.length !== 1) return;

        await new TopicReportHandler(topicDetails.name, selected[0]).requestReport(ReportTypes.TopicAllReport);
        setSuccessMessage(`Your report is being generated and will be emailed to ${props.currentUser?.email} when it's ready.`);
    };

    const onHandleDelete = () => {
        const selectedTopics = props.topicGroups.flatMap(tg => tg.tags.filter(t => selected.includes(t.id)));
        const groups = props.topicGroups.filter(tg => tg.tags.length > 0 && tg.tags.every(t => selected.includes(t.id)));

        const topics = {
            topics: selectedTopics,
            topicGroups: [...groups, ...props.emptyGroupsChecked]
        };

        new TopicDeletionHandler(topics, dispatch).onHandleDelete();
    };

    React.useEffect(() => {
        const handleSingleTopic = async () => {
            const parseAudiences = (audienceIds: string[]) => {
                return props.audiences.filter(a => audienceIds.includes(a.id))
                    ?.map(a => ({ id: a.id, name: a.displayName }));
            };

            const details = await tagsApi.getTopicById(selected[0]);
            const cleanedDetails = {
                id: selected[0],
                name: details.name,
                groupId: details.group,
                disabled: details.disabled,
                default: details.default,
                mandatory: details.mandatory,
                restricted: details.restricted,
                defaultFor: parseAudiences(details.defaultFor),
                mandatoryFor: parseAudiences(details.mandatoryFor),
                restrictedTo: parseAudiences(details.restrictedTo)
            };
            setTopicDetails(cleanedDetails);
        };

        if (selected.length === 1) handleSingleTopic();
    }, [selected, props.audiences]);

    const getToolbarIcon = (title: string, icon: React.ReactNode, onClick: () => void): JSX.Element => {
        return (
            <Button
                aria-label={title.toLowerCase()}
                color="primary"
                startIcon={icon}
                onClick={onClick}>

                {title}
            </Button>
        );
    };

    const isDisabled = (): boolean => topicDetails?.disabled ?? false;

    const itemsSelectedCount = (): number => selected.length + (props.emptyGroupsChecked.length || 0);

    if (!selected.length && props.emptyGroupsChecked.length === 0) return <React.Fragment />;

    return (
        <TabContentToolbar>
            <div>
                {getToolbarIcon(`${itemsSelectedCount()} selected`, <ClearIcon />, props.uncheckAll)}
            </div>

            <div>
                {selected.length === 1 && props.emptyGroupsChecked.length === 0 &&
                    <React.Fragment>
                        {getToolbarIcon("View Details", <VisibilityIcon />, openDetailsDialog)}
                        {getToolbarIcon("Edit", <EditIcon />, openEditDialog)}
                        {getToolbarIcon("Move topic", <DriveFileMoveRoundedIcon />, openMoveDialog)}
                        {getToolbarIcon("Download all reports ", <FileDownloadOutlinedIcon />, handleDownload)}
                        {
                            isDisabled() ?
                                getToolbarIcon("Enable", <CheckCircle />, () => handleStateUpdate(false))
                                : getToolbarIcon("Disable", <CancelOutlinedIcon />, () => handleStateUpdate(true))
                        }
                        {getToolbarIcon("Delete", <DeleteIcon />, onHandleDelete)}
                    </React.Fragment>
                }

                {selected.length > 1 && props.emptyGroupsChecked.length === 0 &&
                    <React.Fragment>
                        {getToolbarIcon("Move topics", <DriveFileMoveRoundedIcon />, openMoveDialog)}
                        {getToolbarIcon("Enable all", <CheckCircle />, () => handleStateUpdate(false))}
                        {getToolbarIcon("Disable all", <CancelOutlinedIcon />, () => handleStateUpdate(true))}
                        {getToolbarIcon("Delete all", <DeleteIcon />, onHandleDelete)}
                    </React.Fragment>
                }
                {
                    props.emptyGroupsChecked.length > 0 &&
                    <React.Fragment>
                        {getToolbarIcon("Delete", <DeleteIcon />, onHandleDelete)}
                    </React.Fragment>
                }
            </div>

            {
                !!topicDetails &&
                <TopicEdit
                    topic={topicDetails}
                    show={editDialogOn}
                    onClose={closeEditDialog} />
            }

            <TopicMove
                toMove={`${selected.length} topics`}
                topics={selected}
                show={moveDialogOn}
                onClose={closeMoveDialog}
            />

            {!!topicDetails &&
                <TopicViewDetails
                    topic={topicDetails}
                    show={topicDetailsOn}
                    onClose={closeDetailsDialog}
                    onEdit={openEditDialog}
                    onMove={openMoveDialog} />
            }

            <SuccessSnackbar
                successMessage={successMessage}
                clearSuccessMessage={() => setSuccessMessage("")}
                autoHideDuration={SNACKBAR_TIME_MS}
            />
        </TabContentToolbar>
    );
};

interface ComponentProps {
    selected: string[],
    topicGroups: TopicGroup[],
    emptyGroupsChecked: TopicGroup[],
    uncheckAll: () => void;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        audiences: state.audiences.audiences,
        currentUser: state.settings.currentUser
    })
);

type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(TopicToolbar);